import { formatMetric } from './panelConfig';

/**
 * Config de la sección "Campañas y anuncios": etiquetas por grupo de objetivo,
 * columnas por tipo, escala de color contextual (peor→mejor dentro del grupo)
 * y formateo. Los códigos (`sales`/`leads`/…) llegan del backend
 * (ObjectiveGroup); acá se traducen a la UI en español.
 */

export const OBJECTIVE_GROUPS = {
  sales: { label: 'Ventas / Conversión', pill: 'bg-emerald-100 text-emerald-800' },
  leads: { label: 'Leads', pill: 'bg-indigo-100 text-indigo-800' },
  traffic: { label: 'Tráfico', pill: 'bg-sky-100 text-sky-800' },
  awareness: { label: 'Awareness', pill: 'bg-amber-100 text-amber-800' },
  engagement: { label: 'Interacción', pill: 'bg-pink-100 text-pink-800' },
  other: { label: 'Otros', pill: 'bg-gray-100 text-gray-700' },
};

export function groupMeta(code) {
  return OBJECTIVE_GROUPS[code] ?? OBJECTIVE_GROUPS.other;
}

export function groupLabel(code) {
  return groupMeta(code).label;
}

const GROUP_TYPE_LABELS = { adset: 'Conjunto', ad_group: 'Grupo', asset_group: 'Asset group' };

export function groupTypeLabel(type) {
  return GROUP_TYPE_LABELS[type] ?? 'Grupo';
}

const COMMON_COLUMNS = [
  { key: 'spend', label: 'Gasto', format: 'currency' },
  { key: 'daily_budget', label: 'Ppto/día', format: 'budget' },
];

// Sólo las métricas que aplican a cada objetivo. `heatmap` marca las columnas
// con escala de color: 'up' = más alto mejor, 'down' = más bajo mejor.
const COLUMNS_BY_GROUP = {
  sales: [
    { key: 'purchases', label: 'Compras', format: 'number' },
    { key: 'purchase_value', label: 'Valor', format: 'currency' },
    { key: 'roas', label: 'ROAS', format: 'ratio', heatmap: 'up' },
    { key: 'cpa', label: 'CPA', format: 'currency', heatmap: 'down' },
  ],
  leads: [
    { key: 'leads', label: 'Leads', format: 'number' },
    { key: 'cpl', label: 'CPL', format: 'currency', heatmap: 'down' },
  ],
  traffic: [
    { key: 'clicks', label: 'Clics', format: 'number' },
    { key: 'cpc', label: 'CPC', format: 'currency', heatmap: 'down' },
    { key: 'ctr', label: 'CTR', format: 'percent', heatmap: 'up' },
  ],
  awareness: [
    { key: 'impressions', label: 'Impresiones', format: 'number' },
    { key: 'reach', label: 'Alcance', format: 'number' },
    { key: 'cpm', label: 'CPM', format: 'currency', heatmap: 'down' },
    { key: 'frequency', label: 'Frecuencia', format: 'decimal' },
  ],
  engagement: [
    { key: 'clicks', label: 'Clics', format: 'number' },
    { key: 'ctr', label: 'CTR', format: 'percent', heatmap: 'up' },
  ],
  other: [
    { key: 'impressions', label: 'Impresiones', format: 'number' },
    { key: 'clicks', label: 'Clics', format: 'number' },
  ],
};

export function columnsForGroup(code) {
  return [...COMMON_COLUMNS, ...(COLUMNS_BY_GROUP[code] ?? COLUMNS_BY_GROUP.other)];
}

// Métrica de cabecera que se muestra junto al Gasto en el header del grupo.
const HEADLINE_BY_GROUP = {
  sales: { label: 'ROAS', key: 'roas', format: 'ratio' },
  leads: { label: 'CPL', key: 'cpl', format: 'currency' },
  traffic: { label: 'CPC', key: 'cpc', format: 'currency' },
  awareness: { label: 'CPM', key: 'cpm', format: 'currency' },
  engagement: { label: 'CTR', key: 'ctr', format: 'percent' },
};

export function headlineMetric(code) {
  return HEADLINE_BY_GROUP[code] ?? null;
}

// Columnas de las vistas planas (Conjuntos / Anuncios): base + la conversión
// relevante según el vertical (ecom = compras + ROAS, lead-gen = leads + CPL).
const FLAT_BASE_COLUMNS = [
  { key: 'spend', label: 'Gasto', format: 'currency' },
  { key: 'impressions', label: 'Impresiones', format: 'number' },
  { key: 'clicks', label: 'Clics', format: 'number' },
  { key: 'ctr', label: 'CTR', format: 'percent' },
];

const FLAT_OUTCOME_COLUMNS = {
  lead_gen: [
    { key: 'leads', label: 'Leads', format: 'number' },
    { key: 'cpl', label: 'CPL', format: 'currency' },
  ],
  ecommerce: [
    { key: 'purchases', label: 'Conversiones', format: 'number' },
    { key: 'roas', label: 'ROAS', format: 'ratio' },
  ],
};

export function flatColumnsFor(vertical) {
  return [...FLAT_BASE_COLUMNS, ...(FLAT_OUTCOME_COLUMNS[vertical] ?? FLAT_OUTCOME_COLUMNS.ecommerce)];
}

export const TONE_CLASSES = {
  good: 'bg-emerald-50 text-emerald-700',
  mid: 'bg-amber-50 text-amber-700',
  bad: 'bg-red-50 text-red-700',
};

/**
 * Posición relativa de `value` entre los `values` del mismo grupo, mapeada a
 * good/mid/bad. `direction` 'down' invierte (menor = mejor). Sin escala útil
 * (una sola campaña o todas iguales) → null (sin color).
 */
export function contextualTone(value, values, direction) {
  if (value == null) return null;
  const nums = (values ?? []).filter((v) => v != null);
  if (nums.length < 2) return null;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  if (max === min) return null;
  let t = (value - min) / (max - min);
  if (direction === 'down') t = 1 - t;
  if (t >= 0.66) return 'good';
  if (t >= 0.33) return 'mid';
  return 'bad';
}

export function formatCell(value, format) {
  if (value === null || value === undefined) return '—';
  if (format === 'decimal') {
    return value.toLocaleString('es-UY', { maximumFractionDigits: 1 });
  }
  return formatMetric(value, format);
}

/**
 * Texto del presupuesto: diario (con nota "conj." si el budget vive a nivel
 * adset/ABO) o total (lifetime). Sin presupuesto → null.
 */
export function budgetInfo(campaign) {
  const b = campaign.budget ?? {};
  if (campaign.daily_budget != null) {
    return { text: formatCell(campaign.daily_budget, 'currency'), hint: b.level === 'adset' ? 'conj.' : null };
  }
  if (b.amount != null && b.type === 'lifetime') {
    return { text: formatCell(b.amount, 'currency'), hint: 'total' };
  }
  return null;
}

// Ordena por `key`; nulls siempre al final sin importar la dirección.
export function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      return dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    }
    return dir === 'asc' ? av - bv : bv - av;
  });
}
